import classNames from "classnames";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { NoObjectChosen } from "../../common/NoObjectChosen";
import { selectFolderItems } from "../../common/Sidebar/redux/selectors";
import { toggleAccordion } from "../../common/Sidebar/redux/slice";
import type { ComparisonObjectValue } from "../../services/api/instances/comparisonAPI/types";
import { createStorageObjectApi } from "../../services/lib/storageObjectManager";
import { getServicePagePath } from "../../services/navigation/navigateToRoute";
import { useAppDispatch } from "../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../services/redux/hooks/use-selector";
import { AddModel } from "./Components/AddModel";
import { ComparisonChat } from "./Components/ComparisonChat";
import { OpenInstructionsButton } from "./Components/OpenInstructionsButton";
import { useCompare } from "./hooks/useCompare";
import {
  createComparisonObjectAction,
  updateComparisonObjectsWithChildrenAction,
} from "./redux/actions";
import {
  selectComparisonObjects,
  selectComparisonValue,
  selectSelectedComparisonObject,
} from "./redux/selectors";
import {
  setComparisonObjects,
  setSelectedComparisonObject,
} from "./redux/slice";
import styles from "./styles.module.css";
import { MessageSendingInput } from "../../common/ChatComponents/MessageSendingInput";
import { SendMessageButton } from "../../common/ChatComponents/MessageSendingInput/Actions/SendMessageButton";
import { StorageObject } from "../../services/api/instances/storageApi/types/object_types";

export const Comparison = () => {
  const dispatch = useAppDispatch();
  const comparisonObjects = useAppSelector(selectComparisonObjects);
  const folderItems = useAppSelector(selectFolderItems);
  const selectedComparisonObject = useAppSelector(
    selectSelectedComparisonObject,
  );
  const objectValue = useAppSelector(selectComparisonValue);
  const {
    userMessage,
    columns,
    instruction,
    setColumns,
    handleKeyDown,
    handleSendMessage,
    addNewChatComparison,
    handleUserMessageChange,
    handleInstructionsChange,
  } = useCompare();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (selectedComparisonObject?.value !== null) {
      setColumns(
        selectedComparisonObject?.value?.llm_compare?.length as number,
      );
    }
  }, [selectedComparisonObject?.value?.llm_compare?.length]);

  const storage =
    createStorageObjectApi<ComparisonObjectValue>(comparisonObjects);

  const addComparisonObject = () => {
    dispatch(createComparisonObjectAction({ isFolder: "object" }))
      .unwrap()
      .then((response: StorageObject<ComparisonObjectValue>) => {
        dispatch(
          updateComparisonObjectsWithChildrenAction({
            childId: response.id,
            parentId: selectedComparisonObject?.id as string,
          }),
        );
        const newComparisonObjects = storage.addItem(
          response,
          selectedComparisonObject?.id,
        );
        dispatch(setComparisonObjects(newComparisonObjects));
        dispatch(setSelectedComparisonObject(response));
        navigate(getServicePagePath(response.id, "llm_compare"));
        if (!folderItems.includes(selectedComparisonObject?.id as string)) {
          dispatch(toggleAccordion(selectedComparisonObject?.id as string));
        }
      });
  };

  return (
    <div className={styles.container}>
      {selectedComparisonObject?.value === null ? (
        <NoObjectChosen
          message={t("dialogs.no_object_message")}
          folderName={selectedComparisonObject.friendly_name}
          onClick={addComparisonObject}
        />
      ) : (
        <div className={styles["comparison-body"]}>
          <div
            className={classNames(
              styles["grid-container"],
              styles[`grid-columns-${columns}`],
            )}
          >
            {objectValue?.map((compare) => (
              <ComparisonChat
                key={compare.id}
                id={compare.id}
                messages={compare?.messages}
                isLoading={compare?.isAssistantChatMessageLoading as boolean}
              />
            ))}
            <AddModel
              onClick={addNewChatComparison}
              className={classNames(styles["add-button"], {
                [styles["none"]]:
                  selectedComparisonObject?.value?.llm_compare.length === 3,
              })}
            />
          </div>
          <MessageSendingInput
            rows={4}
            sendValue={userMessage}
            handleKeyDown={handleKeyDown}
            onChange={handleUserMessageChange}
            inputStyle={"elevated"}
            actions={
              <>
                <OpenInstructionsButton
                  areaValue={
                    objectValue?.find((item) => item.instruction)
                      ?.instruction || instruction
                  }
                  handleChangeInstruction={handleInstructionsChange}
                />
                <SendMessageButton sendMessage={handleSendMessage} />
              </>
            }
          />
        </div>
      )}
    </div>
  );
};
